import { useCallback, useEffect, useState, type RefObject } from "react";

type UseCarouselScrollProps = {
  containerRef: RefObject<HTMLDivElement | null>;
  gapPx: number;
};

type ScrollState = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  totalItems: number;
};

type UseCarouselScrollReturn = {
  updateScrollState: () => void;
  scrollToItem: (index: number) => void;
  scrollState: ScrollState;
  currentSlideIndex: number;
};

export const useCarouselScroll = ({
  containerRef,
  gapPx,
}: UseCarouselScrollProps): UseCarouselScrollReturn => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [scrollState, setScrollState] = useState<ScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
    totalItems: 0,
  });

  const updateScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, children, scrollWidth, clientWidth } = container;

    const maxScrollLeft = scrollWidth - clientWidth;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < maxScrollLeft;

    let visibleIndex = 0;

    if (children.length > 0) {
      const firstChild = children[0];
      if (!firstChild || !(firstChild instanceof HTMLElement)) {
        return;
      }

      // Calculate which item should be visible based on scroll position
      visibleIndex = Math.round(scrollLeft / (firstChild.offsetWidth + gapPx));
      // Ensure index is within bounds
      visibleIndex = Math.max(0, Math.min(visibleIndex, children.length - 1));
    }

    setCurrentSlideIndex(visibleIndex);
    setScrollState({
      canScrollLeft,
      canScrollRight,
      totalItems: children.length,
    });
  }, [containerRef, gapPx]);

  const scrollToItem = useCallback(
    (index: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const children = Array.from(container.children) as HTMLElement[];
      if (index < 0 || index >= children.length) {
        return;
      }

      const firstChild = children[0];
      if (!firstChild) {
        return;
      }
      const targetScrollLeft = index * (firstChild.offsetWidth + gapPx);

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    },
    [containerRef, gapPx],
  );

  // Initialize scroll state on mount and when container changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Initial state update
    updateScrollState();
  }, [updateScrollState, containerRef]);

  return {
    updateScrollState,
    scrollToItem,
    scrollState,
    currentSlideIndex,
  };
};
